import { inject } from 'mobx-react'
import * as React from 'react'
import StepAge from '../StepAge/StepAge'
import StepDayConsumption from '../StepDayConsumption/StepDayConsumption'
import StepFinish from '../StepFinish/StepFinish'
import StepIntro from '../StepIntro/StepIntro'
import StepPrice from '../StepPrice/StepPrice'
import StepSex from '../StepSex/StepSex'
import StepWeekConsumption from '../StepWeekConsumption/StepWeekConsumption'
import StepWeight from '../StepWeight/StepWeight'

import {
  ILayoutStore,
  LOADER_STATUS_IDLE,
  LOADER_STATUS_LOADING,
} from '../../store/LayoutStore'

import { IUserStore } from 'src/store/UserStore'

interface IStepProps {
  autoValidate: boolean
  componentName: string
  isArianeFeedStep: boolean
  LayoutStore?: ILayoutStore
  UserStore?: IUserStore
  isSkippable: boolean
  stepCount: number
  defaultValidate: boolean
  checkConsumptionDays: boolean
}

export interface IStepState {
  displayQuestion: boolean
  isAnswered: boolean
  showNextStep: boolean
  needValidation: boolean
  skipped: boolean
  isSkippable: boolean
  validateAtLeastOnce: boolean
}

// Workaround used to deal with dynamic component instanciation in order to avoid "unused import" TypeScript Import
const mapping = {
  StepAge,
  StepDayConsumption,
  StepFinish,
  StepIntro,
  StepPrice,
  StepSex,
  StepWeekConsumption,
  StepWeight,
}

@inject('LayoutStore', 'UserStore')
class Step extends React.Component<IStepProps, IStepState> {
  private stepEndRef = React.createRef<HTMLDivElement>()

  public constructor(props: IStepProps) {
    super(props)
    this.props.LayoutStore!.changeLoaderStatus(LOADER_STATUS_IDLE)
    this.state = {
      displayQuestion: false,
      isAnswered: this.props.stepCount < this.props.LayoutStore!.currentStep,
      showNextStep: this.props.stepCount >= this.props.LayoutStore!.currentStep,
      needValidation: props.defaultValidate,
      skipped: false,
      isSkippable: props.isSkippable,
      validateAtLeastOnce: false,
    }

    // Handle display of the step 2 sec after loading. Update the status of the loader component too.
    // setTimeout(() => {
    this.props.LayoutStore!.changeLoaderStatus(LOADER_STATUS_LOADING)

    let duration = process.env.REACT_APP_LOADER_DURATION
    if (
      this.props.checkConsumptionDays &&
      this.props.UserStore.consumptionDays.length === 0
    ) {
      duration = '0'
    }
    setTimeout(() => {
      this.setState({ displayQuestion: true })
      this.props.LayoutStore!.changeLoaderStatus(LOADER_STATUS_IDLE)

      if (
        // Step is autoValide like StepIntro
        this.props.autoValidate ||
        // OR User said that he didn't drink at all during the whole week, so we skip the stepWeekConsumption and stepPrice rendering
        (this.props.checkConsumptionDays &&
          this.props.UserStore.consumptionDays.length === 0)
      ) {
        if (this.state.showNextStep) {
          this.props.LayoutStore!.incrementStep()
        }
      }

      if (this.props.isArianeFeedStep) {
        this.props.LayoutStore!.incrementArianeFeedStepCount()
      }
    }, parseInt(duration, 10))
    // }, parseInt(process.env.REACT_APP_LOADER_DURATION, 10) / 2)
  }

  public componentDidMount() {
    window.addEventListener('resize', this.scrollToBottom)
    if (this.state.isAnswered || this.state.skipped) {
      this.setState({ displayQuestion: true })
    }
  }

  public componentDidUpdate() {
    this.scrollToBottom()
  }

  public render() {
    // User said that he didn't drink at all during the whole week, so we skip the stepWeekConsumption and stepPrice rendering
    if (
      this.props.checkConsumptionDays &&
      this.props.UserStore.consumptionDays.length === 0
    ) {
      return null
    }

    // Get the dynamic component based on the component Name from the array in file 'steps.ts'
    const StepComponent = mapping[this.props.componentName]
    return (
      <div className="Step">
        {this.state.displayQuestion && (
          <div>
            <StepComponent
              stepState={this.state}
              onChange={this.onChange}
              onResetAnswer={this.onResetAnswer}
              stepCount={this.props.stepCount}
              onChangeState={this.onChangeState}
            />

            <div
              style={{ float: 'left', clear: 'both' }}
              ref={this.stepEndRef}
            />
          </div>
        )}
      </div>
    )
  }

  private scrollToBottom = () => {
    if (
      this.stepEndRef &&
      this.stepEndRef.current &&
      this.stepEndRef.current.scrollIntoView
    ) {
      this.stepEndRef.current.scrollIntoView()
    }
  }

  /**
   * This function is given as a prop to children component
   * Building it this way, it allows us to handle the stepState in this meta component instead of duplicating states
   * in children components
   */
  private onChange = (change: IStepState) => {
    this.onChangeState(change)
    if (change.hasOwnProperty('skipped') && change.skipped === false) {
      this.props.LayoutStore!.setStep(this.props.stepCount)
      this.props.LayoutStore!.setArianeFeedStepCount(this.props.stepCount)
    } else if (change.isAnswered || change.skipped) {
      this.props.LayoutStore!.incrementStep()
    }
  }

  private onChangeState = (change: IStepState) => {
    this.setState(change)
  }

  /**
   * Same as 'onChange' function, this function is given as a prop in order to call resetAnswer from any step available
   */
  private onResetAnswer = () => {
    this.setState({
      isAnswered: false,
      needValidation: true,
    })
    this.props.LayoutStore!.setStep(this.props.stepCount)
    this.props.LayoutStore!.setArianeFeedStepCount(this.props.stepCount)
    // this.props.UserStore!.resetUserStoreFromStepIndex(2)
  }
}

export default Step



// WEBPACK FOOTER //
// ./src/components/Step/Step.tsx